import { omit } from "lodash";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import PageTitle from "@/react/components/PageTitle";
import { useTable, useDynamicFilters } from "@/react/components/table";
import TableComponent from "@/react/components/table/TableComponent";
import { tenantIdSelector } from "@/react/features/customer/customerSlice";
import ContentWrapper from "@/react/features/styled/PageContentContainer";
import TableWrapper from "@/react/features/styled/TableWrapper";

import columnDefinition from "./columnDefinition";
import { TAP_ID, AGENT_ID_COLUMN_KEY, QUEUE_ID_COLUMN_KEY } from "./constants";
import schema from "./schema";
import useAgentAssignmentsData from "./useAgentAssignmentsData";
import useAgentAssignmentsMutations from "./useAgentAssignmentsMutations";
import { useAssignmentsAutofill } from "./useAssignmentsAutofill";
import { generateAssignmentCountMap } from "@/react/features/vcc/agent-assignments/schemaValidations";

const COLUMN_DEFINITION = columnDefinition();

const AgentAssignmentsPage = () => {
  const location = useLocation();
  const tenantName = useSelector(tenantIdSelector);
  const {
    data: initialAgentAssignments,
    isFetching,
    refetch,
    queryFunctions,
  } = useAgentAssignmentsData();
  const { table, filters, state, mutations } = useTable(
    initialAgentAssignments,
    COLUMN_DEFINITION,
    schema,
    schema.fields.tapId.spec.key,
    {
      ignoreValidationErrors: false,
      queries: queryFunctions,
      validationDataInitializer: generateAssignmentCountMap,
    }
  );
  const { onSave, onCancel, isMutationOngoing, isDirty } =
    useAgentAssignmentsMutations(table, tenantName, mutations);

  /**
   * Apply Agent ID or Queue ID filters based on the location state.
   * This is part of a feature on the Agents page which allows the user
   * to automatically filter for assignments.
   *
   * {@see /vcc/agents/actions/agentActionsColumnFactory.js}
   */
  const applyFilter = useDynamicFilters(table, filters);
  useEffect(() => {
    const agentId = location?.state?.agentId;
    const queueId = location?.state?.queueId;

    if (agentId) {
      applyFilter({
        columnId: schema.fields.agentId.spec.key,
        replace: true,
        filterFnId: "is",
        filterValue: agentId,
      });
    } else if (queueId) {
      applyFilter({
        columnId: schema.fields.queueId.spec.key,
        replace: true,
        filterFnId: "is",
        filterValue: queueId,
      });
    }
    // NOTE: This should only run on component mount, hence why
    // we skip the exhaustive-deps rule for this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autofills agent names if only agent ids are provided and vice versa.
  // Does the same with queue names and ids
  // Therefore, users can upload files using only ids, only names, or both
  const autofillAgentQueueData = useAssignmentsAutofill(
    omit(queryFunctions, ["factory"])
  );

  return (
    <ContentWrapper>
      <PageTitle>Agent Assignments</PageTitle>
      <TableWrapper>
        <TableComponent
          title="Agent Assignments"
          schema={schema}
          isLoading={isFetching || isMutationOngoing}
          onRefresh={refetch}
          table={table}
          filters={filters}
          mutations={mutations}
          onSave={onSave}
          onCancel={onCancel}
          isDirty={isDirty}
          state={state}
          processFileUpload={autofillAgentQueueData}
        />
      </TableWrapper>
    </ContentWrapper>
  );
};

export default AgentAssignmentsPage;
